import { AuthUserPermission, Loading, useOrganizationList, usePermissions } from '@cibo/profile'
import { ThemedButtonListItem } from '@cibo/ui'
import ExpandLessRounded from '@mui/icons-material/ExpandLessRounded'
import ExpandMoreRounded from '@mui/icons-material/ExpandMoreRounded'
import { Box, Collapse, List, ListItemButton, ListItemText } from '@mui/material'
import { NestedNavigationLayout } from 'components/NestedNavigationLayout'
import { ADMIN_ROUTES, ROUTE } from 'navigation/types'
import { Forbidden } from 'pages/Forbidden'
import { prop, sortBy } from 'ramda'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, generatePath, useLocation } from 'react-router-dom'

export const useHasSomeAdminAccess = () => {
  const { can } = usePermissions()
  return can({
    useAny: [
      AuthUserPermission.ORG_DASHBOARD,
      AuthUserPermission.USER_DASHBOARD,
      AuthUserPermission.MANAGE_PROGRAMS,
      AuthUserPermission.MANAGE_AGREEMENTS,
      AuthUserPermission.MANAGE_ENROLLMENTS,
    ],
  })
}

interface ListItem {
  label: string
  to: string
  subItems?: { label: string; to: string }[]
}

export const AdminLayout = () => {
  const { t } = useTranslation('impact/components/AdminLayout')
  const { can, permissions } = usePermissions()
  const organizations = useOrganizationList()
  const hasSomeAdminAccess = useHasSomeAdminAccess()

  const { pathname } = useLocation()

  const pageLinks = useMemo(() => {
    const orgDashboardIfAllowedAndPresent =
      can({ useAny: [AuthUserPermission.ORG_DASHBOARD] }) && !!organizations.data?.length
        ? [
            {
              label: t('organizations'),
              to: ADMIN_ROUTES.ORGANIZATION_LIST,
            },
          ]
        : []
    const userDashboardIfAllowed = can({ useAny: [AuthUserPermission.USER_DASHBOARD] })
      ? [
          {
            label: t('users'),
            to: ADMIN_ROUTES.USER_LIST,
          },
        ]
      : []

    const programs = can({
      useAny: [AuthUserPermission.MANAGE_PROGRAMS, AuthUserPermission.DEBUG_USER],
    })
      ? [
          {
            label: t('programs'),
            to: ADMIN_ROUTES.PROGRAM_LIST,
          },
        ]
      : []

    const agreementSubItems = []

    if (
      can({
        useAny: [
          AuthUserPermission.AGREEMENTS_DATA_VALIDATION_READ,
          AuthUserPermission.AGREEMENTS_DATA_VALIDATION_WRITE,
        ],
      })
    ) {
      agreementSubItems.push({
        label: t('dataValidation'),
        to: generatePath(ADMIN_ROUTES.AGREEMENT_TYPE_LIST, { type: 'data-validation' }),
      })
    }
    if (
      can({
        useAny: [
          AuthUserPermission.AGREEMENTS_OFFER_READ,
          AuthUserPermission.AGREEMENTS_OFFER_WRITE,
          AuthUserPermission.AGREEMENTS_OFFER_CREATE,
        ],
      })
    ) {
      agreementSubItems.push({
        label: t('programOffer'),
        to: generatePath(ADMIN_ROUTES.AGREEMENT_TYPE_LIST, { type: 'program-offer' }),
      })
    }

    if (
      can({
        useAny: [AuthUserPermission.AGREEMENTS_SOIL_READ, AuthUserPermission.AGREEMENTS_SOIL_WRITE],
      })
    ) {
      agreementSubItems.push({
        label: t('soilHealth'),
        to: generatePath(ADMIN_ROUTES.AGREEMENT_TYPE_LIST, { type: 'soil-health' }),
      })
    }

    if (can({ useAny: [AuthUserPermission.AGREEMENTS_ADMIN_REVIEW_READ] })) {
      agreementSubItems.push({
        label: t('errorReport'),
        to: generatePath(ADMIN_ROUTES.AGREEMENT_TYPE_LIST, { type: 'error-report' }),
      })
    }

    const agreements = can({ useAny: [AuthUserPermission.MANAGE_AGREEMENTS] })
      ? [
          {
            label: t('growerManagement'),
            to: ADMIN_ROUTES.GROWER_MANAGEMENT_LIST,
            subItems: sortBy(prop('label'))(agreementSubItems),
          },
        ]
      : []
    return [
      ...programs,
      ...orgDashboardIfAllowedAndPresent,
      ...userDashboardIfAllowed,
      ...(can({ useAny: [AuthUserPermission.MANAGE_ENROLLMENTS] })
        ? [{ label: t('enrollments'), to: ADMIN_ROUTES.ENROLLMENTS_LIST }]
        : []),
      ...agreements,
    ]
  }, [organizations.isFetched, permissions.join()]) //permissions.join is the indirect `can()` dependency

  const value = pageLinks.some(p => pathname === p.to)
    ? pathname
    : pathname
        .split('/')
        .slice(0, ADMIN_ROUTES.ROOT.split('/').length + 1)
        .join('/')

  if (!organizations.isFetched) {
    return <Loading />
  }

  if (!hasSomeAdminAccess) {
    return <Forbidden returnTo={ROUTE.HOME} />
  }

  return (
    <NestedNavigationLayout>
      <Box>
        <List>
          {pageLinks.map((item: ListItem) =>
            !!item.subItems ? (
              <NestedListItem key={item.label} item={item as Required<ListItem>} value={value} />
            ) : (
              <ThemedButtonListItem
                // @ts-ignore the styled version of the list item is not typed with component
                component={Link}
                to={item.to}
                selected={item.to === value}
                data-testid={`menu-${item.label}`}
                key={item.label}
              >
                <ListItemText primary={item.label} />
              </ThemedButtonListItem>
            )
          )}
        </List>
      </Box>
    </NestedNavigationLayout>
  )
}

const NestedListItem = ({ item, value }: { item: Required<ListItem>; value: string }) => {
  const { pathname } = useLocation()
  const [open, setOpen] = useState(true)

  const subValue = item.subItems.some(p => pathname === p.to)
    ? pathname
    : pathname
        .split('/')
        .slice(0, ADMIN_ROUTES.ROOT.split('/').length + 2)
        .join('/')
  const handleClick = () => {
    setOpen(!open)
  }
  return (
    <>
      <ListItemButton
        onClick={handleClick}
        selected={item.to === value}
        data-testid={`menu-${item.label}`}
      >
        <ListItemText primary={item.label} />
        {open ? <ExpandLessRounded /> : <ExpandMoreRounded />}
      </ListItemButton>
      <Collapse in={open} timeout="auto" unmountOnExit>
        <List component="div" disablePadding>
          {item.subItems.map(subItem => (
            <ThemedButtonListItem
              // @ts-ignore the styled version of the list item is not typed with component
              component={Link}
              key={subItem.label}
              to={subItem.to}
              selected={subItem.to === subValue}
              sx={{ pl: 4 }}
              data-testid={`menu-${subItem.label}`}
            >
              <ListItemText primary={subItem.label} />
            </ThemedButtonListItem>
          ))}
        </List>
      </Collapse>
    </>
  )
}
